import { MODULE_CATALOG, type ModuleKey } from '@alicerce/contracts';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Dialog, Field, formatBRL, formatDate, PageHeader, PageLoader, Select, Stat, Table, Td, Th, Tr } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { LoadMore } from '@/components/load-more';
import { PaymentInstructions, type ChargeView } from '@/components/payment-instructions';
import { errorMessage } from '@/lib/api';
import { useCursorList } from '@/lib/hooks';
import { useApi, useCurrent, useSession } from '@/lib/session';
import {
  INTERVAL_LABEL,
  INVOICE_KIND,
  INVOICE_STATUS,
  SUBSCRIPTION_STATUS,
  type AddModuleResult,
  type Interval,
  type Invoice,
  type ModuleOption,
  type ModulePreview,
  type PanelPaymentMethod,
  type PlatformFiscalDocument,
  type SubscriptionOverview,
} from './types';

const per = (i: Interval) => (i === 'YEARLY' ? 'ano' : 'mês');

function MethodSelect({ value, onChange }: { value: PanelPaymentMethod; onChange: (m: PanelPaymentMethod) => void }) {
  return (
    <Field label="Forma de pagamento" hint="Pix libera na hora; o boleto leva até 3 dias úteis.">
      {(a11y) => (
        <Select {...a11y} value={value} onChange={(e) => onChange(e.target.value as PanelPaymentMethod)}>
          <option value="PIX">Pix</option>
          <option value="BOLETO">Boleto</option>
        </Select>
      )}
    </Field>
  );
}

export function BillingPage() {
  const api = useApi();
  const q = useQuery({ queryKey: ['billing', 'subscription'], queryFn: () => api.get<SubscriptionOverview>('/v1/billing/subscription') });
  return (
    <div className="space-y-6">
      <PageHeader
        title="Assinatura"
        description="Seu plano, módulos e faturas."
        actions={
          <Link to="/assinatura/consumo" className="text-sm font-medium text-brand-700 hover:underline">
            Consumo e créditos
          </Link>
        }
      />
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <Alert tone="danger">{errorMessage(q.error)}</Alert>
      ) : (
        <>
          <OverviewCard sub={q.data} />
          <ModulesCard sub={q.data} />
          <InvoicesCard />
          <PlatformNotesCard />
        </>
      )}
    </div>
  );
}

function OverviewCard({ sub }: { sub: SubscriptionOverview }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { refreshMe } = useSession();
  const { can } = useCurrent();
  const manage = can('billing:manage');
  const [dialog, setDialog] = useState<'subscribe' | 'cancel' | null>(null);
  const status = SUBSCRIPTION_STATUS[sub.tenantStatus];
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['billing'] });
    await refreshMe();
  };
  const changeInterval = useMutation({ mutationFn: (interval: Interval) => api.patch('/v1/billing/subscription', { interval }), onSuccess: refresh });
  const reactivate = useMutation({ mutationFn: () => api.post('/v1/billing/subscription/reactivate'), onSuccess: refresh });
  const error = changeInterval.error ?? reactivate.error;

  return (
    <Card>
      <CardHeader title="Seu plano" actions={<Badge tone={status.tone}>{status.label}</Badge>} />
      <CardBody className="space-y-4">
        {error && <Alert tone="danger">{errorMessage(error)}</Alert>}
        {sub.tenantStatus === 'TRIAL' && sub.trialEndsAt && (
          <Alert tone="info" title={`Seu teste vai até ${formatDate(sub.trialEndsAt)}.`}>
            {sub.hasPaymentMethod ? 'A primeira cobrança sai quando o teste acabar.' : 'Escolha como pagar para continuar usando depois disso.'}
          </Alert>
        )}
        {sub.tenantStatus === 'PAST_DUE' && (
          <Alert tone="warning" title="Não recebemos o último pagamento.">
            Pague a fatura em aberto abaixo para não perder o acesso.
          </Alert>
        )}
        {sub.tenantStatus === 'READ_ONLY' && (
          <Alert tone="danger" title="Sua conta está em modo somente leitura.">
            Você ainda vê tudo, mas não consegue mudar nada. Pague a fatura em aberto para voltar ao normal.
          </Alert>
        )}
        {sub.cancelAtPeriodEnd && sub.currentPeriodEnd && (
          <Alert
            tone="warning"
            title={`A assinatura termina em ${formatDate(sub.currentPeriodEnd)}.`}
            action={
              manage && (
                <Button size="sm" loading={reactivate.isPending} onClick={() => reactivate.mutate()}>
                  Manter assinatura
                </Button>
              )
            }
          >
            Depois disso a conta fica só para leitura por 90 dias e então os dados são apagados.
          </Alert>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label={`Total por ${per(sub.interval)}`} value={formatBRL(sub.recurringTotalCents)} hint={sub.bundle ? `Pacote ${sub.bundle.name} (−${sub.bundle.discountPercent}%)` : undefined} />
          <Stat
            label="Próxima cobrança"
            value={sub.currentPeriodEnd && !sub.cancelAtPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}
            hint={sub.nextInterval && sub.nextInterval !== sub.interval ? `Passa a ser ${INTERVAL_LABEL[sub.nextInterval].toLowerCase()} na renovação` : undefined}
          />
          <div className="space-y-1">
            {manage && sub.status !== 'CANCELED' ? (
              <Field label="Ciclo de cobrança" hint="O anual sai mais barato. A troca vale a partir da renovação.">
                {(a11y) => (
                  <Select {...a11y} disabled={changeInterval.isPending} value={sub.nextInterval ?? sub.interval} onChange={(e) => changeInterval.mutate(e.target.value as Interval)}>
                    <option value="MONTHLY">Mensal</option>
                    <option value="YEARLY">Anual</option>
                  </Select>
                )}
              </Field>
            ) : (
              <Stat label="Ciclo" value={INTERVAL_LABEL[sub.interval]} />
            )}
          </div>
        </div>
        {manage && (
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            {(!sub.hasPaymentMethod || sub.status === 'CANCELED') && <Button onClick={() => setDialog('subscribe')}>Escolher como pagar</Button>}
            {sub.hasPaymentMethod && !sub.cancelAtPeriodEnd && sub.status !== 'CANCELED' && (
              <Button variant="ghost" onClick={() => setDialog('cancel')}>
                Cancelar assinatura
              </Button>
            )}
          </div>
        )}
      </CardBody>
      {dialog === 'subscribe' && <SubscribeDialog sub={sub} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'cancel' && <CancelDialog onClose={() => setDialog(null)} onDone={refresh} />}
    </Card>
  );
}

function SubscribeDialog({ sub, onClose, onDone }: { sub: SubscriptionOverview; onClose: () => void; onDone: () => Promise<void> }) {
  const api = useApi();
  const [method, setMethod] = useState<PanelPaymentMethod>('PIX');
  const [interval, setInterval] = useState<Interval>(sub.interval);
  const subscribe = useMutation({
    mutationFn: () => api.post<{ quote: { totalCents: number }; charge: ChargeView | null }>('/v1/billing/subscription', { method, interval }),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} title="Escolher como pagar">
      {subscribe.data ? (
        <div className="space-y-4">
          {subscribe.data.charge ? (
            <PaymentInstructions charge={subscribe.data.charge} title="Primeira cobrança" />
          ) : (
            <Alert tone="success">Pronto. A primeira cobrança sai quando o teste acabar e chega por e-mail.</Alert>
          )}
          <div className="flex justify-end">
            <Button onClick={onClose}>Fechar</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label="Ciclo">
            {(a11y) => (
              <Select {...a11y} value={interval} onChange={(e) => setInterval(e.target.value as Interval)}>
                <option value="MONTHLY">Mensal</option>
                <option value="YEARLY">Anual</option>
              </Select>
            )}
          </Field>
          <MethodSelect value={method} onChange={setMethod} />
          {subscribe.isError && <Alert tone="danger">{errorMessage(subscribe.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button loading={subscribe.isPending} onClick={() => subscribe.mutate()}>
              Confirmar
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function CancelDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { refreshMe } = useSession();
  const cancel = useMutation({
    mutationFn: () => api.post<{ cancelAt: string | null; downgradeOptions: { moduleKey: ModuleKey; name: string; savingsCents: number }[] }>('/v1/billing/subscription/cancel'),
    onSuccess: onDone,
  });
  const reactivate = useMutation({ mutationFn: () => api.post('/v1/billing/subscription/reactivate'), onSuccess: onDone });
  const remove = useMutation({
    mutationFn: (key: ModuleKey) => api.delete(`/v1/billing/modules/${key}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
      await refreshMe();
    },
  });
  const result = cancel.data;
  return (
    <Dialog open onClose={onClose} title={result ? 'Assinatura cancelada' : 'Cancelar a assinatura?'}>
      {result ? (
        <div className="space-y-4 text-sm">
          <p>Você continua usando tudo até {formatDate(result.cancelAt)}. Mudou de ideia? Dá para manter a assinatura até lá.</p>
          {result.downgradeOptions.length > 0 && (
            <div className="space-y-2">
              <p className="font-medium">Se o preço pesou, tire só o que você menos usa e mantenha o resto:</p>
              <ul className="space-y-2">
                {result.downgradeOptions.map((o) => (
                  <li key={o.moduleKey} className="flex items-center justify-between gap-2 rounded-lg border border-line p-3">
                    <span>
                      Tirar {o.name} <span className="text-muted">economiza {formatBRL(o.savingsCents)}</span>
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={remove.isPending && remove.variables === o.moduleKey}
                      onClick={async () => {
                        await remove.mutateAsync(o.moduleKey);
                        await reactivate.mutateAsync();
                        onClose();
                      }}
                    >
                      Tirar e continuar
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(remove.error ?? reactivate.error) && <Alert tone="danger">{errorMessage(remove.error ?? reactivate.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" loading={reactivate.isPending && !remove.isPending} onClick={() => reactivate.mutate(undefined, { onSuccess: onClose })}>
              Manter assinatura
            </Button>
            <Button onClick={onClose}>Fechar</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <p>Você usa tudo até o fim do período já pago. Depois a conta fica só para leitura por 90 dias, e então os dados são apagados.</p>
          {cancel.isError && <Alert tone="danger">{errorMessage(cancel.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancelar assinatura
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function ModulesCard({ sub }: { sub: SubscriptionOverview }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { refreshMe } = useSession();
  const { can } = useCurrent();
  const manage = can('billing:manage');
  const [params, setParams] = useSearchParams();
  const adding = params.get('adicionar') as ModuleKey | null;
  const modules = useQuery({ queryKey: ['billing', 'modules'], queryFn: () => api.get<ModuleOption[]>('/v1/billing/modules') });
  const remove = useMutation({
    mutationFn: (key: ModuleKey) => api.delete<{ removeAt: string }>(`/v1/billing/modules/${key}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
      await refreshMe();
    },
  });
  const itemOf = (key: string) => sub.items.find((i) => i.moduleKey === key);
  const closeAdd = () => setParams((p) => {
    p.delete('adicionar');
    return p;
  });

  return (
    <Card>
      <CardHeader title="Módulos" description="Pague só pelo que usa. Ao adicionar, você paga a diferença dos dias que faltam no ciclo." />
      {remove.isError && (
        <div className="px-5 pt-4">
          <Alert tone="danger">{errorMessage(remove.error)}</Alert>
        </div>
      )}
      {modules.isPending ? (
        <PageLoader />
      ) : modules.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(modules.error)}</Alert>
        </CardBody>
      ) : (
        <ul className="divide-y divide-line">
          {modules.data.map((m) => {
            const item = itemOf(m.key);
            const leaving = item?.status === 'PENDING_REMOVAL';
            return (
              <li key={m.key} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                  <p className="font-medium">
                    {m.name}{' '}
                    {m.active && !leaving && <Badge tone="success">ativo</Badge>}
                    {leaving && <Badge tone="warning">sai em {formatDate(item.removeAt)}</Badge>}
                  </p>
                  <p className="text-sm text-muted">{MODULE_CATALOG[m.key]?.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">
                    {item ? formatBRL(item.totalCents) : m.amountCents !== null ? formatBRL(m.amountCents) : '—'}/{per(m.interval)}
                  </span>
                  {manage &&
                    (m.active && !leaving ? (
                      <Button size="sm" variant="ghost" loading={remove.isPending && remove.variables === m.key} onClick={() => remove.mutate(m.key)}>
                        Remover
                      </Button>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => setParams({ adicionar: m.key })}>
                        {leaving ? 'Manter' : 'Adicionar'}
                      </Button>
                    ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {adding && manage && MODULE_CATALOG[adding] && <AddModuleDialog module={adding} onClose={closeAdd} />}
    </Card>
  );
}

export function AddModuleDialog({ module, onClose }: { module: ModuleKey; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { refreshMe } = useSession();
  const [method, setMethod] = useState<PanelPaymentMethod>('PIX');
  const preview = useQuery({ queryKey: ['billing', 'preview', module], queryFn: () => api.get<ModulePreview>(`/v1/billing/modules/${module}/preview`) });
  const add = useMutation({
    mutationFn: () => api.post<AddModuleResult>(`/v1/billing/modules/${module}`, { method }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
      await refreshMe();
    },
  });
  const name = MODULE_CATALOG[module].name;
  const p = preview.data;
  return (
    <Dialog open onClose={onClose} title={`Adicionar ${name}`}>
      {add.data ? (
        <div className="space-y-4">
          {add.data.status === 'ACTIVE' ? (
            <Alert tone="success">{name} já está ativo.</Alert>
          ) : add.data.charge ? (
            <PaymentInstructions charge={add.data.charge} title="Pague para ativar" description="O módulo é liberado assim que o pagamento for confirmado." />
          ) : null}
          <div className="flex justify-end">
            <Button onClick={onClose}>Fechar</Button>
          </div>
        </div>
      ) : preview.isPending ? (
        <PageLoader />
      ) : preview.isError ? (
        <Alert tone="danger">{errorMessage(preview.error)}</Alert>
      ) : (
        <div className="space-y-4 text-sm">
          <p>{MODULE_CATALOG[module].description}</p>
          {p!.addedDependencies.length > 0 && (
            <Alert tone="info" title={`Também entra: ${p!.added.filter((k) => k !== module).map((k) => MODULE_CATALOG[k as ModuleKey]?.name ?? k).join(', ')}`}>
              {p!.addedDependencies.map((d) => d.reason).join(' ')}
            </Alert>
          )}
          <dl className="space-y-1 rounded-lg bg-canvas p-3">
            <div className="flex justify-between">
              <dt>A mais por {per(p!.quote.interval)}</dt>
              <dd className="font-medium tabular-nums">{formatBRL(p!.recurringDiffCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Novo total por {per(p!.quote.interval)}</dt>
              <dd className="tabular-nums">{formatBRL(p!.quote.totalCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Agora ({p!.remainingDays} de {p!.cycleDays} dias)</dt>
              <dd className="font-medium tabular-nums">{p!.prorataCents > 0 ? formatBRL(p!.prorataCents) : 'sem custo'}</dd>
            </div>
          </dl>
          {p!.prorataCents > 0 && <MethodSelect value={method} onChange={setMethod} />}
          {add.isError && <Alert tone="danger">{errorMessage(add.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button loading={add.isPending} onClick={() => add.mutate()}>
              Adicionar {name}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function InvoicesCard() {
  const list = useCursorList<Invoice>(['billing', 'invoices'], '/v1/billing/invoices', { limit: 12 });
  return (
    <Card>
      <CardHeader title="Faturas" />
      {list.isPending ? (
        <PageLoader />
      ) : list.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(list.error)}</Alert>
        </CardBody>
      ) : list.items.length === 0 ? (
        <CardBody>
          <p className="text-sm text-muted">Nenhuma fatura ainda.</p>
        </CardBody>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Vencimento</Th>
                <Th>Tipo</Th>
                <Th>Valor</Th>
                <Th>Situação</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {list.items.map((i) => (
                <Tr key={i.id}>
                  <Td>{formatDate(i.dueDate)}</Td>
                  <Td>{INVOICE_KIND[i.kind] ?? i.kind}</Td>
                  <Td className="tabular-nums">{formatBRL(i.amountCents)}</Td>
                  <Td>
                    <Badge tone={INVOICE_STATUS[i.status]?.tone ?? 'neutral'}>{INVOICE_STATUS[i.status]?.label ?? i.status}</Badge>
                  </Td>
                  <Td className="text-right">
                    {i.paymentUrl && (i.status === 'PENDING' || i.status === 'OVERDUE') && (
                      <a href={i.paymentUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-700 hover:underline">
                        Pagar
                      </a>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <LoadMore {...list} />
        </>
      )}
    </Card>
  );
}

function PlatformNotesCard() {
  const api = useApi();
  const q = useQuery({ queryKey: ['billing', 'nfse'], queryFn: () => api.get<PlatformFiscalDocument[]>('/v1/billing/fiscal-documents') });
  const files = useMutation({
    mutationFn: (id: string) => api.get<{ pdfUrl: string | null; xmlUrl: string | null }>(`/v1/billing/fiscal-documents/${id}/files`),
    onSuccess: (f) => {
      const url = f.pdfUrl ?? f.xmlUrl;
      if (url) window.open(url, '_blank', 'noopener');
    },
  });
  if (q.isPending || q.isError || q.data.length === 0) return null;
  return (
    <Card>
      <CardHeader title="Notas fiscais da Gestora" description="As NFS-e que emitimos pelas suas faturas." />
      {files.isError && (
        <div className="px-5 pt-4">
          <Alert tone="danger">{errorMessage(files.error)}</Alert>
        </div>
      )}
      <Table>
        <thead>
          <tr>
            <Th>Número</Th>
            <Th>Descrição</Th>
            <Th>Valor</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {q.data.map((d) => (
            <Tr key={d.id}>
              <Td>{d.number ?? '—'}</Td>
              <Td>{d.description}</Td>
              <Td className="tabular-nums">{formatBRL(d.amountCents)}</Td>
              <Td className="text-right">
                {d.hasFiles && (
                  <Button size="sm" variant="ghost" loading={files.isPending && files.variables === d.id} onClick={() => files.mutate(d.id)}>
                    Baixar PDF
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
